import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GetCustomFields,
  setCatcustomFields,
} from "../../../redux/slices/CustomFieldsSlice";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { setSelectedBusinessDetails } from "../../../redux/slices/bwvSlice";

const BusinessFilter = ({ selectedCat }) => {
  const dispatch = useDispatch();
  const { catCustomFields, loadingCustomFields, errorCustomFields } =
    useSelector((state) => state.customFields);
  const selectedBusinessDetails = useSelector(
    (state) => state.bwvData.selectedBusinessDetails
  );

  const [openQuestions, setOpenQuestions] = useState({});
  const [selected, setSelected] = useState({}); // { questionId: { option: true } }
  const restoredRef = useRef(false);

  // Load custom fields
  useEffect(() => {
    if (!selectedCat) {
      dispatch(setCatcustomFields([]));
      return;
    }
    dispatch(GetCustomFields(selectedCat.Id));
  }, [selectedCat, dispatch]);

  // Restore from string ONCE
  useEffect(() => {
    if (
      restoredRef.current ||
      loadingCustomFields ||
      !catCustomFields?.length ||
      typeof selectedBusinessDetails !== "string" ||
      !selectedBusinessDetails.trim()
    )
      return;

    const saved = selectedBusinessDetails
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    if (!saved.length) return;

    const map = {};
    catCustomFields.forEach((field) => {
      const options = parseCustomValue(field.customValue);
      const fieldMap = {};
      saved.forEach((val) => {
        if (options.includes(val)) fieldMap[val] = true;
      });
      if (Object.keys(fieldMap).length) map[field.id] = fieldMap;
    });

    setSelected(map);
    restoredRef.current = true;
  }, [catCustomFields, selectedBusinessDetails, loadingCustomFields]);

  const toggleQuestion = (id) => {
    setOpenQuestions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleOption = (questionId, value) => {
    setSelected((prev) => {
      const current = prev[questionId] || {};
      const newField = {
        ...current,
        [value]: !(current[value] ?? false),
      };

      // Remove empty field
      const cleaned = Object.fromEntries(
        Object.entries({ ...prev, [questionId]: newField }).filter(
          ([_, opts]) => Object.values(opts).some(Boolean)
        )
      );

      // Build final string
      const allSelected = Object.values(cleaned)
        .flatMap((opts) => Object.keys(opts).filter((k) => opts[k]))
        .join(",");

      dispatch(setSelectedBusinessDetails(allSelected));

      return cleaned;
    });
  };

  const selectedCount = (questionId) => {
    const map = selected[questionId] || {};
    return Object.values(map).filter(Boolean).length;
  };

  const parseCustomValue = (customValue) => {
    if (!customValue) return [];
    try {
      const parsed = JSON.parse(customValue);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  // Loading / Error
  if (loadingCustomFields) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
      >
        <div
          style={{
            width: "2rem",
            height: "2rem",
            border: "3px solid #e5e7eb",
            borderTop: "3px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      </div>
    );
  }

  if (errorCustomFields) {
    return (
      <div style={{ color: "#ef4444", textAlign: "center", padding: "1rem" }}>
        Error loading fields.
      </div>
    );
  }

  if (!selectedCat || !catCustomFields?.length) {
    return (
      <div style={{ color: "#6b7280", textAlign: "center", padding: "1rem" }}>
        No custom fields available.
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "400px",
        margin: "0 auto",
        padding: "1rem",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {catCustomFields.map((field) => {
        const isOpen = openQuestions[field.id];
        const options = parseCustomValue(field.customValue);
        const count = selectedCount(field.id);

        return (
          <div key={field.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
            <div
              onClick={() => toggleQuestion(field.id)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                padding: "0.75rem 0",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#2563eb")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#111827")}
            >
              <h6
                style={{
                  margin: 0,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "#111827",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {isOpen ? (
                  <FaChevronDown size={13} style={{ color: "#6b7280" }} />
                ) : (
                  <FaChevronRight size={13} style={{ color: "#6b7280" }} />
                )}
                {field.Question}
                {count > 0 && (
                  <span
                    style={{
                      marginLeft: "0.5rem",
                      backgroundColor: "#1f2937",
                      color: "#fff",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      padding: "0.15rem 0.45rem",
                      borderRadius: "9999px",
                      minWidth: "1.2rem",
                      textAlign: "center",
                    }}
                  >
                    {count}
                  </span>
                )}
              </h6>
            </div>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ paddingLeft: "1.5rem" }}>
                    {options.map((option, idx) => {
                      const checked = !!selected[field.id]?.[option];
                      return (
                        <label
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            cursor: "pointer",
                            padding: "5px 0",
                            borderRadius: "6px",
                            transition: "background 0.2s",
                            userSelect: "none",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f9fafb")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              "transparent")
                          }
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOption(field.id, option)}
                            style={{
                              width: "16px",
                              height: "16px",
                              accentColor: "#b700d6",
                              cursor: "pointer",
                              borderRadius: "4px",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "0.875rem",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            {option}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export default BusinessFilter;
